import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ApplicationForm from "../components/ApplicationForm/ApplicationForm";
import { usePortal } from "../PortalContext";
import { getApplicationDetail } from "@/services/universityService";
import { Loader2 } from "lucide-react";

export default function CreateApplication() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { applications, resumeStep, resumeAppNumber, handleApplicationSubmit, handleComplete } = usePortal();

    const applicationId = searchParams.get("id") ?? undefined;
    const urlStep = searchParams.get("step") ? Number(searchParams.get("step")) : undefined;
    const preselectedTerm = searchParams.get("term") ?? undefined;
    const preselectedDegree = searchParams.get("degree") ?? undefined;
    const preselectedProgram = searchParams.get("program") ? Number(searchParams.get("program")) : undefined;
    // New multi-select param: comma-separated ordered program IDs
    // De-duplicate while preserving order so the backend never receives duplicate selections.
    const preselectedProgramsParam = searchParams.get("programs");
    const preselectedProgramsList: number[] | undefined = preselectedProgramsParam
        ? Array.from(
              new Set(
                  preselectedProgramsParam
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n) && n > 0)
              )
          )
        : undefined;
    const preselectedApplicationType = searchParams.get("applicationType") ?? undefined;

    const [draftTerm, setDraftTerm] = useState<string | undefined>(preselectedTerm);
    const [draftDegree, setDraftDegree] = useState<string | undefined>(preselectedDegree);
    const [draftProgram, setDraftProgram] = useState<number | undefined>(
        preselectedProgramsList?.length ? preselectedProgramsList[0] : preselectedProgram
    );
    const [draftPrograms, setDraftPrograms] = useState<number[] | undefined>(
        preselectedProgramsList?.length
            ? preselectedProgramsList
            : preselectedProgram != null
                ? [preselectedProgram]
                : undefined
    );
    const [draftApplicationType, setDraftApplicationType] = useState<string | undefined>(preselectedApplicationType);
    const [detailLoaded, setDetailLoaded] = useState(!applicationId);
    const [detailFound, setDetailFound] = useState(true);

    useEffect(() => {
        if (!applicationId) return;
        getApplicationDetail(applicationId)
            .then((detail) => {
                if (detail.status === "success") {
                    setDraftTerm(detail.term_id ?? undefined);
                    setDraftDegree(detail.degree_type ?? detail.degree_level ?? undefined);
                    if (detail.interested_programs?.length) {
                        setDraftPrograms(detail.interested_programs);
                        setDraftProgram(detail.interested_programs[0]);
                    } else if (detail.price_list_entry_id) {
                        setDraftProgram(detail.price_list_entry_id);
                        setDraftPrograms([detail.price_list_entry_id]);
                    }
                    if (detail.application_type) setDraftApplicationType(detail.application_type);
                } else {
                    setDetailFound(false);
                }
            })
            .catch(() => { setDetailFound(false); })
            .finally(() => setDetailLoaded(true));
    }, [applicationId]);

    // Sync resume step once applications list and detail are both ready
    useEffect(() => {
        if (!applicationId || !detailLoaded) return;
        const existing = applications.find(
            (app) => app.Application_Number === applicationId
        );
        if (existing) {
            const isComplete = existing.Action === "Complete";
            if (isComplete) {
                handleComplete(existing);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, applications.length, detailLoaded]);

    const handleCancel = () => {
        navigate("/portal/applications");
    };

    const handleApplicationCreated = useCallback((appNumber: string) => {
        window.history.replaceState(null, "", `/portal/create-application?id=${appNumber}&step=1`);
    }, []);

    const handleStepChange = useCallback((appNumber: string, step: number) => {
        window.history.replaceState(null, "", `/portal/create-application?id=${appNumber}&step=${step}`);
    }, []);

    if (!detailLoaded) return (
        <div
            className="flex items-center justify-center min-h-[60vh]"
            role="status"
            aria-live="polite"
            aria-label="Loading application"
        >
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="sr-only">Loading application…</span>
        </div>
    );

    const resolvedAppNumber = detailFound ? (applicationId ?? resumeAppNumber) : resumeAppNumber;
    const resolvedStep = detailFound ? (urlStep ?? (applicationId ? (resumeStep ?? 0) : 0)) : 0;

    return (
        <ApplicationForm
            onCancel={handleCancel}
            onSubmit={handleApplicationSubmit}
            onApplicationCreated={handleApplicationCreated}
            onStepChange={handleStepChange}
            initialStep={resolvedStep}
            applicationNumber={resolvedAppNumber}
            initialTerm={draftTerm}
            initialDegree={draftDegree}
            initialProgram={draftProgram}
            initialPrograms={draftPrograms}
            initialApplicationType={draftApplicationType}
        />
    );
}
